import _ from 'underscore';

import {TagSchema} from 'constants/RegistryConstants';
import {Source} from 'constants/SourceConstants';
import {Tag} from 'folder-items/FileConstants';
import {Section as SidebarSection} from 'sidebar/SidebarConstants';

import {FolderSummary} from './FolderConstants';

export enum Section {
  AI = 'AI',
  ANALYTICS = 'ANALYTICS',
  BENCHLING_APPS = 'BENCHLING_APPS',
  CONNECTIONS = 'CONNECTIONS',
  COMPLEX_POLYMERS = 'COMPLEX_POLYMERS',
  DEVELOPER = 'DEVELOPER',
  HOME = 'HOME',
  ONBOARDING_CHECKLISTS = 'ONBOARDING_CHECKLISTS',
  JELLYFISH = 'JELLYFISH', // App Core fake item type
  MOD_PATTERNS = 'MOD_PATTERNS',
  MOLBIO_ANTIBODY_NESTED_DATA_CREATION = 'MOLBIO_ANTIBODY_NESTED_DATA_CREATION',
  MOLBIO_BIOINTELLIGENT_LINK_COMPONENT_REPLACE = 'MOLBIO_BIOINTELLIGENT_LINK_COMPONENT_REPLACE',
  MOLBIO_BULK_ASSEMBLY = 'MOLBIO_BULK_ASSEMBLY',
  MODEL_HUB_STRUCTURE_PREDICTION = 'MODEL_HUB_STRUCTURE_PREDICTION',
  PROCESSES = 'PROCESSES',
  PROCEDURE_RUN = 'PROCEDURE_RUN',
  PROJECTS = 'PROJECTS',
  PROJECT_ITEMS = 'PROJECT_ITEMS',
  REGISTRIES = 'REGISTRIES',
  REQUESTS = 'REQUESTS',
  REQUESTS_V2_SUBMISSIONS = 'REQUESTS_V2_SUBMISSIONS',
  REQUESTS_V2_DEFINITIONS = 'REQUESTS_V2_DEFINITIONS',
  SEARCH = 'SEARCH',
  STORAGE = 'STORAGE',
  STUDY_ITEMS = 'STUDY_ITEMS',
  STUDIES = 'STUDIES',
  TEMPLATE_COLLECTIONS = 'TEMPLATE_COLLECTIONS',
  TFI = 'TFI',
  WORKFLOWS = 'WORKFLOWS',
  WORKLISTS = 'WORKLISTS',
  WORKLIST_ITEMS = 'WORKLIST_ITEMS',
  WORKSHEET = 'WORKSHEET',
}

export const SECTIONS = Section;
export type SidepanelSection = Section | SidebarSection;
// This is the list of sections which are displayed on the "main" Benchling page.
// This means they will not navigate away from the tabbed workspace.
export const STANDARD_SECTIONS = [
  SECTIONS.AI,
  SECTIONS.ANALYTICS,
  SECTIONS.BENCHLING_APPS,
  SECTIONS.CONNECTIONS,
  SECTIONS.HOME,
  SECTIONS.JELLYFISH,
  SECTIONS.PROCESSES,
  SECTIONS.PROJECTS,
  SECTIONS.PROJECT_ITEMS,
  SECTIONS.REGISTRIES,
  SECTIONS.REQUESTS,
  SECTIONS.REQUESTS_V2_SUBMISSIONS,
  SECTIONS.REQUESTS_V2_DEFINITIONS,
  SECTIONS.SEARCH,
  SECTIONS.STORAGE,
  SECTIONS.STUDIES,
  SECTIONS.TEMPLATE_COLLECTIONS,
  SECTIONS.TFI,
  SECTIONS.WORKFLOWS,
  SECTIONS.WORKLISTS,
  SECTIONS.WORKLIST_ITEMS,
];

export enum SidepanelExpansionState {
  /** Sidepanel is not visible; workspace takes all space to the right of the nav bar. */
  CLOSED = 'CLOSED',
  /** Sidepanel is a thin box on the far left, with only a button showing. */
  COLLAPSED = 'COLLAPSED',
  /** Functional elements visible on sidepanel; workspace also visible. */
  SEMI_EXPANDED = 'SEMI_EXPANDED',
  /** Functional elements visible on sidepanel but with more room; workspace also visible. */
  SEMI_EXPANDED_PLUS = 'SEMI_EXPANDED_PLUS',
  /** Sidepanel expanded to full-page view, obscuring workspace. */
  FULLY_EXPANDED = 'FULLY_EXPANDED',
}

export type ProjectsSidepanelSection = Section.PROJECTS | Section.PROJECT_ITEMS;

export function isProjectsSidepanelSection(section: SidepanelSection): section is ProjectsSidepanelSection {
  return section === Section.PROJECTS || section === Section.PROJECT_ITEMS;
}

export interface Listed {
  id: string;
  loading?: boolean;
  name: string;
  parent: FolderSummary;
  source: Source;
  url: string;
}

export interface ExpandedTags {
  showColumn: boolean;
  tagColumns: Array<string>;
}

export interface SearchParams {
  baseScope?: string | null;
  filter: string | null;
  q: string | null;
  scope: string | null;
}

export interface FinderResult {
  id: string;
  name: string;
  parent?: FolderSummary;
  tags: Array<Tag>;
  tagSchema: TagSchema | null;
  folder: {api_identifier: string};
  selected?: boolean;
  notePreview?: string;
  deleting?: boolean;
}

export interface WorkspaceItem {
  fileId: string;
}

export const PROJECTS_FINDER_ID = 'finder_projects';
export const REGISTRIES_FINDER_ID = 'finder_registries';
export const SEARCH_FINDER_ID = 'finder_search';
export const JELLYFISHES_FINDER_ID = 'finder_jellyfishes';
export const WORKLIST_ITEMS_FINDER_ID = 'finder_worklist_items';
export const CONNECTIONS_FINDER_ID = 'finder_connections';
export const PROCESSES_FINDER_ID = 'finder_processes';
export const STUDIES_FINDER_ID = 'finder_studies';
export const STUDY_ITEMS_FINDER_ID = 'finder_study_items';
export const REQUESTS_V2_SUBMISSIONS_FINDER_ID = 'finder_requests_v2_submissions';
export const REQUESTS_V2_DEFINITIONS_FINDER_ID = 'finder_requests_v2_definitions';
export const DEEP_RESEARCH_FINDER_ID = 'finder_deep_research';
export const FINDER_ID_TO_SECTION: {[finderId: string]: Section} = {
  [PROJECTS_FINDER_ID]: Section.PROJECT_ITEMS,
  [REGISTRIES_FINDER_ID]: Section.REGISTRIES,
  [SEARCH_FINDER_ID]: Section.SEARCH,
  [WORKLIST_ITEMS_FINDER_ID]: Section.WORKLIST_ITEMS,
  [PROCESSES_FINDER_ID]: Section.PROCESSES,
  [CONNECTIONS_FINDER_ID]: Section.CONNECTIONS,
  [JELLYFISHES_FINDER_ID]: Section.JELLYFISH,
  [STUDIES_FINDER_ID]: Section.STUDIES,
  [STUDY_ITEMS_FINDER_ID]: Section.STUDY_ITEMS,
  [REQUESTS_V2_SUBMISSIONS_FINDER_ID]: Section.REQUESTS_V2_SUBMISSIONS,
  [REQUESTS_V2_DEFINITIONS_FINDER_ID]: Section.REQUESTS_V2_DEFINITIONS,
};
export const SECTION_TO_FINDER_ID = _.invert(FINDER_ID_TO_SECTION) as {[K in Section]: string};

export const NavPreferences = {
  SIDEPANEL_IS_DISPLAY_COMPACT: 'sidepanelIsCompactDisplay',
  SIDEPANEL_IS_OPEN: 'sidepanelIsOpen',
  SIDEPANEL_LAST_OPENED_SECTION: 'sidepanelLastOpenedSection',
};

export const REQUESTS_V2_SIDEPANEL_TAB_KEY = 'requests-v2-sidepanel-tab';
